// src/modules/helpers/GeneratedItemsList.js
import React from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { IonIcon } from "@ionic/react";
import {
  chevronDown,
  chevronForward,
  eyeOutline,
  eyeOffOutline,
  trashOutline,
  copyOutline
} from "ionicons/icons";
import { getAllSettings, getAllScripts } from "../../data/getters";
import Item from "./item.js";
import { useLanguage } from "./LanguageContext";
import "../../css/GeneratedItemsList.css";

export default function GeneratedItemsList({
  calledFrom,
  onRegenerate,
  onSettingChange,
  generatedItems,
  palette,
  collapsedColors,
  toggleColor,
  handleItemChange,
  handleItemColorChange,
  onDragEnd,
  onDelete,
  onDeleteObject,
  onDuplicateObject,
  readOnly = false,
  objects = [],
  onSwap,
  hiddenItems,
  onToggleVisibility,
  onItemColorSelect,
  onAddItemToObject,
  defaultCollapsedObjectIds,
  defaultHiddenObjectIds
}) {
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const allSettingsRaw =
    dispatch(useSelector(getAllSettings, shallowEqual)) || [];

  const allScriptsRaw = useSelector(
    (state) => getAllScripts(state),
    shallowEqual
  );

  const allSettings = Array.isArray(allSettingsRaw) ? allSettingsRaw : [];
  const allScripts = Array.isArray(allScriptsRaw) ? allScriptsRaw : [];

  const settingNameById = React.useMemo(() => {
    const m = {};

    allSettings.forEach((s) => {
      m[String(s.settingId)] = s.name;
    });

    return m;
  }, [allSettings]);

  const scriptById = React.useMemo(() => {
    const m = {};

    allScripts.forEach((s) => {
      m[String(s.scriptId)] = s;
    });

    return m;
  }, [allScripts]);

  const [localSettings, setLocalSettings] = React.useState({});

  const objectsSignatureRef = React.useRef("");

  React.useEffect(() => {
    const sig = objects.map((o) => `${o.objectId}:${o.setting}`).join("|");

    if (sig === objectsSignatureRef.current) return;

    objectsSignatureRef.current = sig;

    setLocalSettings((prev) => {
      const next = { ...prev };

      objects.forEach((obj) => {
        const oid = String(obj.objectId);
        const parsed = parseObjectSetting(obj) ?? {};
        next[oid] = parsed;
      });

      return next;
    });
  }, [objects]);

  const initializedHiddenObjectsRef = React.useRef(new Set());

  function toggleObjectVisibility(oid, items) {
    const itemIds = items.map((it) => getItemId(it));
    const isCurrentlyHidden = hiddenObjects.has(oid);

    itemIds.forEach((itemId) => {
      const isHidden = hiddenItems ? hiddenItems.has(itemId) : false;

      if (isCurrentlyHidden && isHidden) {
        onToggleVisibility?.(itemId);
      } else if (!isCurrentlyHidden && !isHidden) {
        onToggleVisibility?.(itemId);
      }
    });
  }

  function parseObjectSetting(obj) {
    if (!obj?.setting) return null;

    if (typeof obj.setting === "object") {
      return obj.setting;
    }

    try {
      return JSON.parse(obj.setting);
    } catch {
      return null;
    }
  }

  const DIM_KEYS = ["width", "height", "depth"];

  const DIM_LABELS = {
    width: t("dimWidth"),
    height: t("dimHeight"),
    depth: t("dimDepth")
  };

  function getAllowedIds(scriptSetting) {
    if (!scriptSetting) return null;

    return scriptSetting
      .split(",")
      .map((p) => p.split(":")[0].trim())
      .filter((k) => k && !DIM_KEYS.includes(k));
  }

  function parseValueList(raw) {
    if (!raw) return null;

    const str = Array.isArray(raw) ? raw.join(",") : String(raw);

    if (!str.trim()) return null;

    return str.split(",").map((tok) => {
      tok = tok.trim();

      const lastColon = tok.lastIndexOf(":");

      if (lastColon > 0) {
        return {
          label: tok.slice(0, lastColon).trim(),
          value: tok.slice(lastColon + 1).trim()
        };
      }

      return {
        label: tok,
        value: tok
      };
    });
  }

  function handleSettingFieldChange(oid, key, value) {
    setLocalSettings((prev) => ({
      ...prev,
      [oid]: {
        ...(prev[oid] ?? {}),
        [key]: value
      }
    }));
  }

  function handleSettingBlur(oid) {
    const newSetting = localSettings[oid] ?? {};

    onSettingChange?.(oid, newSetting);
    onRegenerate?.(oid, newSetting);
  }

  function renderSettings(obj) {
    const oid = String(obj?.objectId);
    const setting = localSettings[oid] ?? parseObjectSetting(obj) ?? {};

    if (Object.keys(setting).length === 0) {
      return (
        <p
          style={{
            margin: 0,
            color: "#aaa",
            fontStyle: "italic",
            fontSize: "0.8rem"
          }}
        >
          {t("noSettings")}
        </p>
      );
    }

    const scriptId = obj?.usedScript?.scriptId;
    const script = scriptId != null ? scriptById[String(scriptId)] : null;
    const allowedIds = getAllowedIds(script?.setting ?? null);

    const entries = Object.entries(setting).filter(([key]) => {
      if (DIM_KEYS.includes(key)) return true;
      if (allowedIds === null) return true;

      return allowedIds.includes(key);
    });

    if (entries.length === 0) {
      return (
        <p
          style={{
            margin: 0,
            color: "#aaa",
            fontStyle: "italic",
            fontSize: "0.8rem"
          }}
        >
          {t("noSettings")}
        </p>
      );
    }

    const inputStyle = {
      border: "none",
      borderBottom: "1px solid #ddd",
      background: "transparent",
      outline: "none",
      fontSize: "0.8rem",
      padding: "1px 4px",
      width: "100%",
      textAlign: "right",
      color: "#333",
      fontWeight: 500
    };

    const selectStyle = {
      ...inputStyle,
      cursor: "pointer",
      appearance: "auto"
    };

    return (
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.8rem"
        }}
      >
        <tbody>
          {entries.map(([key, value]) => {
            const isDim = DIM_KEYS.includes(key);

            const label = isDim
              ? DIM_LABELS[key]
              : (settingNameById[String(key)] ?? `#${key}`);

            const meta = isDim
              ? null
              : allSettings.find((s) => String(s.settingId) === String(key));

            const opts = parseValueList(meta?.valueList ?? null);

            const curVal = String(localSettings[oid]?.[key] ?? value ?? "");

            return (
              <tr key={key} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td
                  style={{
                    padding: "3px 8px 3px 4px",
                    color: "#777",
                    whiteSpace: "nowrap",
                    width: "55%"
                  }}
                >
                  {t(label)}
                </td>

                <td style={{ padding: "2px 4px" }}>
                  {opts ? (
                    <select
                      style={selectStyle}
                      value={curVal}
                      onChange={(e) => {
                        handleSettingFieldChange(oid, key, e.target.value);
                        setTimeout(() => handleSettingBlur(oid), 0);
                      }}
                      disabled={readOnly}
                    >
                      {opts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {t(o.label)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      style={inputStyle}
                      value={curVal}
                      onChange={(e) =>
                        handleSettingFieldChange(oid, key, e.target.value)
                      }
                      onBlur={() => handleSettingBlur(oid)}
                      disabled={readOnly}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  const itemsByObject = React.useMemo(() => {
    return generatedItems.reduce((acc, item, idx) => {
      const rawOid = item.object?.objectId ?? item.objectId;
      const oid = rawOid != null ? String(rawOid) : "no-object";

      acc[oid] = acc[oid] || [];
      acc[oid].push({
        ...item,
        __idx: idx
      });

      return acc;
    }, {});
  }, [generatedItems]);

  const getItemColor = (itm) => {
    const rawCid = itm.color?.colorId ?? itm.colorId;

    if (rawCid == null) return null;

    return palette.find((c) => String(c.colorId) === String(rawCid)) || null;
  };

  function getItemId(it) {
    const id = it.itemId ?? it.tmpId;

    return id != null ? id : `tmp-${it.__idx}`;
  }

  const hiddenObjects = React.useMemo(() => {
    const hidden = new Set();

    Object.entries(itemsByObject).forEach(([oid, items]) => {
      if (
        items.length > 0 &&
        items.every((item) => hiddenItems?.has(getItemId(item)))
      ) {
        hidden.add(oid);
      }
    });

    return hidden;
  }, [itemsByObject, hiddenItems]);

  React.useEffect(() => {
    if (!defaultHiddenObjectIds?.length) return;

    defaultHiddenObjectIds.forEach((rawId) => {
      const oid = String(rawId);
      const items = itemsByObject[oid];
      if (!items?.length || initializedHiddenObjectsRef.current.has(oid))
        return;

      initializedHiddenObjectsRef.current.add(oid);
      toggleObjectVisibility(oid, items);
    });
  }, [defaultHiddenObjectIds, hiddenObjects, itemsByObject]);

  const [selectedItemIds, setSelectedItemIds] = React.useState(new Set());
  const lastSelectedIdRef = React.useRef(null);

  const allItemsFlatRef = React.useRef([]);

  React.useEffect(() => {
    allItemsFlatRef.current = Object.values(itemsByObject)
      .flat()
      .map((it) => getItemId(it));
  }, [itemsByObject]);

  function toggleSelected(itemId, shiftKey = false) {
    const flat = allItemsFlatRef.current;

    if (shiftKey && lastSelectedIdRef.current != null) {
      const fromIdx = flat.indexOf(lastSelectedIdRef.current);
      const toIdx = flat.indexOf(itemId);

      if (fromIdx !== -1 && toIdx !== -1) {
        const [start, end] =
          fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];

        const rangeIds = flat.slice(start, end + 1);

        setSelectedItemIds((prev) => {
          const next = new Set(prev);
          const shouldDeselect = prev.has(itemId);

          rangeIds.forEach((id) => {
            if (shouldDeselect) {
              next.delete(id);
            } else {
              next.add(id);
            }
          });

          return next;
        });
      }
    } else {
      setSelectedItemIds((prev) => {
        const next = new Set(prev);

        if (next.has(itemId)) {
          next.delete(itemId);
        } else {
          next.add(itemId);
        }

        return next;
      });
    }

    lastSelectedIdRef.current = itemId;
  }

  const [collapsedObjects, setCollapsedObjects] = React.useState({});

  React.useEffect(() => {
    if (!defaultCollapsedObjectIds?.length) return;

    setCollapsedObjects((prev) => {
      let changed = false;
      const next = { ...prev };

      defaultCollapsedObjectIds.forEach((rawId) => {
        const oid = String(rawId);
        if (Object.prototype.hasOwnProperty.call(next, oid)) return;
        next[oid] = true;
        changed = true;
      });

      return changed ? next : prev;
    });
  }, [defaultCollapsedObjectIds]);

  const toggleObject = (oid) => {
    setCollapsedObjects((prev) => ({
      ...prev,
      [oid]: !prev[oid]
    }));

    const oids = (itemsByObject[oid] || []).map((it) => getItemId(it));

    setSelectedItemIds((prev) => {
      const next = new Set(prev);

      oids.forEach((id) => next.delete(id));

      return next;
    });
  };

  const [activeTabs, setActiveTabs] = React.useState({});

  const getTab = (oid) => activeTabs[oid] ?? "items";

  const setTab = (oid, tab) => {
    setActiveTabs((prev) => ({
      ...prev,
      [oid]: tab
    }));

    if (tab !== "items") {
      const oids = (itemsByObject[oid] || []).map((it) => getItemId(it));

      setSelectedItemIds((prev) => {
        const next = new Set(prev);

        oids.forEach((id) => next.delete(id));

        return next;
      });
    }
  };

  return (
    <div className="generated-items-list">
      <div>
        {Object.entries(itemsByObject).map(([oid, items], groupIdx) => {
          const obj = objects.find((o) => String(o.objectId) === oid);
          const isCollapsed = !!collapsedObjects[oid];
          const activeTab = getTab(oid);
          const isObjectHidden = hiddenObjects.has(oid);

          const objDisplayName = obj
            ? `#${oid} ${t(obj.name)}`
            : `${t("objPrefix")}${oid}`;

          return (
            <div
              key={oid}
              className={[
                "generated-items-list__object-group",
                groupIdx === 0 && "is-first"
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {/* ── Objektum fejléc ── */}
              <div
                className="generated-items-list__object-header"
                onClick={() => toggleObject(oid)}
              >
                <IonIcon
                  icon={isCollapsed ? chevronForward : chevronDown}
                  className="generated-items-list__object-chevron"
                />

                <span
                  className={[
                    "generated-items-list__object-name",
                    isObjectHidden && "is-hidden"
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {objDisplayName}
                </span>

                <span className="generated-items-list__item-count">
                  {items.length} {t("itemCount")}
                </span>

                {!isCollapsed &&
                  activeTab === "items" &&
                  (() => {
                    const oids = items.map((it) => getItemId(it));

                    const allSelected =
                      oids.length > 0 &&
                      oids.every((id) => selectedItemIds.has(id));

                    const someSelected =
                      !allSelected &&
                      oids.some((id) => selectedItemIds.has(id));

                    const toggle = (e) => {
                      e.stopPropagation();

                      setSelectedItemIds((prev) => {
                        const next = new Set(prev);

                        if (allSelected) {
                          oids.forEach((id) => next.delete(id));
                        } else {
                          oids.forEach((id) => next.add(id));
                        }

                        return next;
                      });
                    };

                    return (
                      <div
                        onClick={toggle}
                        title={allSelected ? t("deselectAll") : t("selectAll")}
                        className="generated-items-list__select-all"
                      >
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someSelected;
                            }
                          }}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                          className="generated-items-list__select-all-checkbox"
                        />
                      </div>
                    );
                  })()}

                {!readOnly && (
                  <button
                    className={[
                      "generated-items-list__icon-btn",
                      isObjectHidden && "is-active"
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    title={isObjectHidden ? t("showObject") : t("hideObject")}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleObjectVisibility(oid, items);
                    }}
                  >
                    <IonIcon
                      icon={isObjectHidden ? eyeOffOutline : eyeOutline}
                      className="generated-items-list__header-icon"
                    />
                  </button>
                )}

                {!readOnly &&
                  onDuplicateObject &&
                  !objects.find((o) => String(o.objectId) === String(oid))
                    ?.system && (
                    <button
                      type="button"
                      className="generated-items-list__icon-btn"
                      title="Objektum duplikálása az elemeivel"
                      aria-label="Objektum duplikálása az elemeivel"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicateObject(Number(oid));
                      }}
                    >
                      <IonIcon
                        icon={copyOutline}
                        className="generated-items-list__header-icon"
                      />
                    </button>
                  )}
                {!readOnly && onDeleteObject && (
                  <button
                    className="generated-items-list__icon-btn generated-items-list__icon-btn--danger"
                    title={t("deleteObject")}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteObject(Number(oid));
                    }}
                  >
                    <IonIcon
                      icon={trashOutline}
                      className="generated-items-list__header-icon"
                    />
                  </button>
                )}

                {!isCollapsed && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="generated-items-list__tabs"
                  >
                    <button
                      className={[
                        "generated-items-list__tab",
                        activeTab === "items" && "is-active"
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTab(oid, "items");
                      }}
                    >
                      {t("itemsTab")}
                    </button>

                    <button
                      className={[
                        "generated-items-list__tab",
                        activeTab === "settings" && "is-active"
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTab(oid, "settings");
                      }}
                    >
                      {t("settingsTab")}
                    </button>
                  </div>
                )}
              </div>

              {!isCollapsed && activeTab === "items" && (
                <div className="generated-items-list__add-item-row">
                  <button
                    onClick={() => onAddItemToObject?.(Number(oid))}
                    className="generated-items-list__add-item-button"
                  >
                    {t("addNewItem")}
                  </button>
                </div>
              )}

              {!isCollapsed &&
                activeTab === "items" &&
                items.map((itWithIdx) => {
                  const { __idx, ...itm } = itWithIdx;
                  const itemId = getItemId(itWithIdx);
                  const dragId = `item-${String(itemId)}`;
                  const itemColor = getItemColor(itm);
                  const isSelected = selectedItemIds.has(itemId);

                  return (
                    <div
                      key={dragId}
                      className={[
                        "generated-items-list__item-row",
                        isSelected && "is-selected"
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <Item
                        calledFrom={calledFrom}
                        Item={itm}
                        index={dragId}
                        readOnly={readOnly}
                        color={itemColor}
                        checked={isSelected}
                        onCheckedChange={(_, e) =>
                          toggleSelected(itemId, e?.shiftKey ?? false)
                        }
                        onColorClick={() => {
                          setSelectedItemIds((prev) => {
                            const n = new Set(prev);
                            n.add(itemId);
                            return n;
                          });

                          onItemColorSelect?.(
                            new Set([...selectedItemIds, itemId]),
                            null
                          );
                        }}
                        visible={hiddenItems ? !hiddenItems.has(itemId) : true}
                        onToggleVisibility={() => onToggleVisibility(itemId)}
                        onItemChange={(upd) =>
                          !readOnly && handleItemChange(__idx, upd)
                        }
                        onDelete={() => !readOnly && onDelete(__idx)}
                      />
                    </div>
                  );
                })}

              {!isCollapsed && activeTab === "settings" && (
                <div className="generated-items-list__settings">
                  {renderSettings(obj)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
